// Single source of truth for DFI18231 study-access capabilities.
//
// Derives the DFI18231 role booleans from the pharma `flags` array that is already
// stored by PharmaProvider (no new sessionStorage keys), then expresses every gate
// as a study-aware ternary. When `isDfiUser` is false, each capability MUST evaluate
// to exactly the legacy SIENNA/PHARMA1 condition, so non-DFI behavior is byte-for-byte
// unchanged.
//
// DFI role flags (arrive verbatim in user.flags as clinic_flags):
//   DFI18231_SITE, DFI18231_CRC, DFI18231_STUDY_TEAM, DFI18231_DEVICE_ADMIN, DFI18231_VV
//   (VV also carries DFI18231_DEVICE_ADMIN; STUDY_TEAM also carries READONLY.)
import { useMemo } from 'react';

import { useAuth } from '@/lib/auth';

import { usePharma } from './PharmaProvider';

export type Capabilities = {
  // DFI18231 role flags
  isDfiSite: boolean;
  isDfiCrc: boolean;
  isDfiStudyTeam: boolean;
  isDfiDeviceAdmin: boolean;
  isDfiVv: boolean;
  isDfiUser: boolean;
  isActingAsSite: boolean;
  // Study-access capability gates
  canConfigureTests: boolean;
  canAssignHeadset: boolean;
  canUnassignHeadset: boolean;
  canUploadImaging: boolean;
  canDesignLayouts: boolean;
  canEditParticipant: boolean;
  canToggleOutlier: boolean;
  canGenerateReportPdf: boolean;
  canDownloadDesignImages: boolean;
  canManageDevicesPage: boolean;
  canViewHomeTestDetails: boolean;
  canSeeImagingStatus: boolean;
};

export const useCapabilities = (): Capabilities => {
  const { flags, isCRC, isPV, isREADONLY } = usePharma();
  // The auth bootstrap (is_logged_in) hydrates auth.user independently of the
  // Pharma provider: on a fresh tab pharmaFlags can be empty/stale while
  // auth.user.flags carries the server-reported session flags. The staff
  // hard-fail must therefore consider BOTH sources, or a raw staff session
  // restored into a fresh tab would slip past the pharma-only check.
  const { user: authUser } = useAuth();
  const rawSiteStaff =
    flags.includes('DFI18231_SITE_STAFF') ||
    (authUser?.flags ?? []).includes('DFI18231_SITE_STAFF');

  return useMemo(() => {
    // DFI18231 Site Staff hard-fail (defense-in-depth backstop).
    // A Site Staff login is resolved server-side to run AS its bound Site, so a
    // correctly-resolved session carries DFI18231_SITE and NEVER exposes this raw flag.
    // If DFI18231_SITE_STAFF reaches this SPA, the server-side resolve FAILED - an
    // impossible-if-resolve-worked, broken/unauthorized state. Without this guard the
    // unknown DFI flag would make isDfiUser === true while isDfiSite === false, which
    // yields canViewHomeTestDetails === true (an UN-BLINDED session - the exact hazard).
    // Lock EVERYTHING down instead - every capability false, especially
    // canViewHomeTestDetails - so a broken resolve can never leak Home outcomes.
    if (rawSiteStaff) {
      return {
        isDfiSite: false,
        isDfiCrc: false,
        isDfiStudyTeam: false,
        isDfiDeviceAdmin: false,
        isDfiVv: false,
        isDfiUser: false,
        isActingAsSite: false,
        canConfigureTests: false,
        canAssignHeadset: false,
        canUnassignHeadset: false,
        canUploadImaging: false,
        canDesignLayouts: false,
        canEditParticipant: false,
        canToggleOutlier: false,
        canGenerateReportPdf: false,
        canDownloadDesignImages: false,
        canManageDevicesPage: false,
        canViewHomeTestDetails: false,
        canSeeImagingStatus: false,
      };
    }

    const isDfiSite = flags.includes('DFI18231_SITE');
    const isDfiCrc = flags.includes('DFI18231_CRC');
    const isDfiStudyTeam = flags.includes('DFI18231_STUDY_TEAM');
    const isDfiDeviceAdmin = flags.includes('DFI18231_DEVICE_ADMIN');
    const isDfiVv = flags.includes('DFI18231_VV');
    const isDfiUser = flags.some((f) => f.startsWith('DFI18231_'));

    return {
      isDfiSite,
      isDfiCrc,
      isDfiStudyTeam,
      isDfiDeviceAdmin,
      isDfiVv,
      isDfiUser,
      // "Acting as site" = reached the app via the act-as-site selector (shows the banner +
      // "Back to Site Selection"). SIENNA: isCRC (byte-for-byte). DFI: the acting roles
      // (CRC / Study Team / VV); NOT a direct Site login or Device Admin.
      isActingAsSite: isDfiUser ? isDfiCrc || isDfiStudyTeam || isDfiVv : isCRC,
      // study-aware: non-DFI branch === today's exact condition.
      // DFI: VV operators can also edit/save participant test configurations
      // (support interventions), alongside the Site that administers the tests.
      canConfigureTests: isDfiUser ? isDfiSite || isDfiVv : !isREADONLY,
      canAssignHeadset: isDfiUser ? isDfiSite || isDfiVv : isCRC,
      canUnassignHeadset: isDfiUser ? isDfiSite || isDfiVv : !isREADONLY,
      // DFI: VV operators also manage retinal images (alongside CRC); the localizer
      // app authenticates with the effective-site u/p and audits via crc_name, so no
      // change is needed on that side.
      canUploadImaging: isDfiUser ? isDfiCrc || isDfiVv : isCRC,
      canDesignLayouts: isDfiUser ? isDfiVv : isCRC,
      canEditParticipant: isDfiUser ? isDfiVv : !isREADONLY,
      canToggleOutlier: isDfiUser ? isDfiVv : true,
      canGenerateReportPdf: isDfiUser ? isDfiVv : isCRC,
      canDownloadDesignImages: isDfiUser ? isDfiVv : isCRC,
      canManageDevicesPage: isDfiUser ? isDfiDeviceAdmin || isDfiVv : isPV,
      // DFI18231 blinding: only a DFI Site is barred from opening/downloading Home test
      // results/reports and seeing Home outcome values. Everyone else (incl. DFI CRC/StudyTeam/
      // VV/DeviceAdmin and all SIENNA roles) is unaffected.
      canViewHomeTestDetails: isDfiUser ? !isDfiSite : true,
      // BSR "Img" column (retinal-imaging readiness): for the people who process
      // imaging and design layouts — not part of the site-facing report. DFI: CRC
      // (reading center) + VV. Non-DFI branch mirrors the sienna-branch gate
      // (dc8557a): any pharma flag containing '_VV' or '_CRC'.
      canSeeImagingStatus: isDfiUser
        ? isDfiCrc || isDfiVv
        : flags.some((f) => f.includes('_VV') || f.includes('_CRC')),
    };
  }, [flags, isCRC, isPV, isREADONLY, rawSiteStaff]);
};
